import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Notice } from '@/components/ui/Feedback'
import { NativeSelect } from '@/components/ui/NativeSelect'
import { formatDay } from '@/lib/format'
import type { AdminFetch } from './AdminCustomers'
import { authorLabels, categoryLabels, channelLabels, messageStatusLabels, type SupportCategory, type SupportChannel } from './adminSupportText'

export type SupportMessage = {
  id: string; direction: 'IN' | 'OUT'; author: 'CUSTOMER' | 'AI' | 'HUMAN' | 'SYSTEM'; status: string; subject?: string | null; body: string
  originalDraft?: string | null; edited: boolean; createdAt: string; sentAt?: string | null; lastError?: string | null
}
export type SupportDetail = {
  conversation: { id: string; channel: SupportChannel; status: 'OPEN' | 'CLOSED'; category?: SupportCategory | null; needsHuman: boolean; subject?: string | null; createdAt: string }
  contact: { id: string; kind: 'EMAIL' | 'PHONE' | 'USER' | 'WEB' | 'TELEGRAM'; identity: string; displayName?: string | null }
  contactLabel: string
  linked: { userEmail?: string | null; userName?: string | null; companyId?: string | null; businessName?: string | null; hosting?: string | null; provisioningStatus?: string | null; swingBusiness?: string | null; swingExpiresOn?: string | null; quoteBusiness?: string | null; quoteStatus?: string | null }
  messages: SupportMessage[]
}

const when = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
const hostingLabels: Record<string, string> = { MANAGED_CLOUD: 'Nube' }

/**
 * One conversation: what the contact is on the platform, what was said, the draft waiting for approval with its
 * editor, and the answer a person writes. Every answer leaves by the conversation's channel when it is sent.
 */
export function AdminSupportConversation({ detail, adminFetch, describeError, onChanged }: {
  detail: SupportDetail; adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string; onChanged: (updated: SupportDetail) => void
}) {
  const { conversation, contact, linked, messages } = detail
  const draft = messages.find((message) => message.status === 'DRAFT')
  const [draftText, setDraftText] = useState(draft?.body ?? '')
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState<'approve' | 'discard' | 'reply' | 'status' | 'redraft' | 'category' | null>(null)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [closing, setClosing] = useState(false)
  const heading = useRef<HTMLHeadingElement>(null)

  // Another conversation starts clean; a draft that arrives or changes fills its editor without touching the notices.
  useEffect(() => { setReply(''); setError(''); setNotice(''); requestAnimationFrame(() => heading.current?.focus()) }, [conversation.id])
  useEffect(() => { setDraftText(draft?.body ?? '') }, [draft?.id, draft?.body])

  async function run(kind: NonNullable<typeof busy>, action: () => Promise<SupportDetail>, done: string, failed: string) {
    setBusy(kind); setError(''); setNotice('')
    try {
      const updated = await action()
      if (kind === 'reply') setReply('')
      onChanged(updated); setNotice(done)
    } catch (cause) { setError(describeError(cause, failed)) } finally { setBusy(null) }
  }

  function approve(event: FormEvent) {
    event.preventDefault()
    if (!draft) return
    const body = draftText.trim()
    if (!body) { setError('Escribe la respuesta antes de aprobarla.'); return }
    void run('approve', () => adminFetch<SupportDetail>(`/admin/support/drafts/${draft.id}/approve`, { method: 'POST', body: { body } }), 'Respuesta enviada.', 'No pudimos enviar la respuesta.')
  }

  function send(event: FormEvent) {
    event.preventDefault()
    const body = reply.trim()
    if (!body) { setError('Escribe la respuesta.'); return }
    void run('reply', () => adminFetch<SupportDetail>(`/admin/support/conversations/${conversation.id}/messages`, { method: 'POST', body: { body } }), 'Respuesta enviada.', 'No pudimos enviar la respuesta.')
  }

  function close() {
    void run('status', () => adminFetch<SupportDetail>(`/admin/support/conversations/${conversation.id}`, { method: 'PATCH', body: { status: 'CLOSED' } }), 'Conversación cerrada.', 'No pudimos cerrar la conversación.')
    setClosing(false)
  }

  const who = contact.displayName ?? linked.userName ?? null

  return <article className="admin-customer-detail admin-support-detail" aria-labelledby="admin-support-conversation">
    <header className="admin-customer-heading">
      <div><h3 id="admin-support-conversation" ref={heading} tabIndex={-1}>{who ?? detail.contactLabel}</h3>
        <p className="field-hint">{channelLabels[conversation.channel]}{who && ` · ${detail.contactLabel}`} · Abierta el {when.format(new Date(conversation.createdAt))}{conversation.needsHuman && ' · Pide una persona'}</p>
        <div className="admin-support-category"><NativeSelect label="Categoría" value={conversation.category ?? ''} disabled={busy !== null}
          onChange={(event) => void run('category', () => adminFetch<SupportDetail>(`/admin/support/conversations/${conversation.id}`, { method: 'PATCH', body: { category: event.target.value } }), 'Categoría guardada.', 'No pudimos guardar la categoría.')}>
          <option value="">Sin categoría</option>
          {(Object.keys(categoryLabels) as SupportCategory[]).map((category) => <option key={category} value={category}>{categoryLabels[category]}</option>)}
        </NativeSelect></div></div>
      {conversation.status === 'OPEN'
        ? <Button emphasis="ghost" intent="neutral" busy={busy === 'status'} onClick={() => setClosing(true)}>Cerrar conversación</Button>
        : <Button emphasis="outline" intent="neutral" busy={busy === 'status'} onClick={() => void run('status', () => adminFetch<SupportDetail>(`/admin/support/conversations/${conversation.id}`, { method: 'PATCH', body: { status: 'OPEN' } }), 'Conversación abierta de nuevo.', 'No pudimos abrir la conversación.')}>Abrir de nuevo</Button>}
    </header>
    {(linked.businessName || linked.swingBusiness || linked.quoteBusiness || linked.userEmail) && <section className="admin-customer-block" aria-label="Quién es en la plataforma">
      <h4>En la plataforma</h4>
      <ul className="admin-support-links">
        {linked.userEmail && <li>Cuenta: {linked.userName ? `${linked.userName}, ` : ''}{linked.userEmail}</li>}
        {linked.businessName && <li>Empresa: {linked.businessName}{linked.hosting && ` (${hostingLabels[linked.hosting] ?? linked.hosting}${linked.provisioningStatus ? `, ${linked.provisioningStatus.toLowerCase()}` : ''})`}</li>}
        {linked.swingBusiness && <li>Servidor de Swing: {linked.swingBusiness}{linked.swingExpiresOn && `, vence el ${formatDay(linked.swingExpiresOn)}`}</li>}
        {linked.quoteBusiness && <li>Solicitud de cotización: {linked.quoteBusiness}{linked.quoteStatus === 'ATTENDED' ? ', atendida' : ', nueva'}</li>}
      </ul>
    </section>}
    {notice && <Notice tone="success">{notice}</Notice>}
    {error && <Notice>{error}</Notice>}
    <ol className="admin-support-thread" aria-label="Mensajes">
      {messages.filter((message) => message.status !== 'DRAFT').map((message) => <li key={message.id} data-direction={message.direction} data-status={message.status}>
        <div className="admin-support-meta"><strong>{authorLabels[message.author]}</strong> · {when.format(new Date(message.createdAt))}{message.direction === 'OUT' && ` · ${messageStatusLabels[message.status] ?? message.status}`}{message.edited && ' · editado'}</div>
        {message.subject && <p className="admin-support-subject">{message.subject}</p>}
        <p className="admin-support-body">{message.body}</p>
        {message.lastError && <p className="field-error">{message.lastError}</p>}
      </li>)}
    </ol>
    {draft
      ? <form noValidate onSubmit={approve} className="admin-support-draft">
        <div className="form-field">
          <label htmlFor="admin-support-draft">Borrador del asistente</label>
          <textarea id="admin-support-draft" rows={6} maxLength={8000} value={draftText} onChange={(event) => setDraftText(event.target.value)} disabled={busy !== null} />
          <p className="field-hint">Revísalo, corrígelo si hace falta y apruébalo: sale por {channelLabels[conversation.channel].toLowerCase()} tal como quede.</p>
        </div>
        <div className="admin-support-actions"><Button type="submit" busy={busy === 'approve'}>Aprobar y enviar</Button>
          <Button emphasis="outline" intent="neutral" busy={busy === 'discard'} onClick={() => void run('discard', () => adminFetch<SupportDetail>(`/admin/support/drafts/${draft.id}/discard`, { method: 'POST' }), 'Borrador descartado.', 'No pudimos descartar el borrador.')}>Descartar</Button>
          <Button emphasis="ghost" intent="neutral" busy={busy === 'redraft'} onClick={() => void run('redraft', () => adminFetch<SupportDetail>(`/admin/support/conversations/${conversation.id}/redraft`, { method: 'POST' }), 'El asistente redacta otro borrador; aparecerá en unos segundos.', 'No pudimos pedir otro borrador.')}>Generar de nuevo</Button></div>
      </form>
      : <form noValidate onSubmit={send} className="admin-support-draft">
        <div className="form-field">
          <label htmlFor="admin-support-reply">Responder</label>
          <textarea id="admin-support-reply" rows={4} maxLength={8000} value={reply} onChange={(event) => setReply(event.target.value)} disabled={busy !== null} />
          <p className="field-hint">Sale al instante por {channelLabels[conversation.channel].toLowerCase()}.</p>
        </div>
        <div className="admin-support-actions"><Button type="submit" busy={busy === 'reply'}>Enviar respuesta</Button>
          {conversation.status === 'OPEN' && messages.some((message) => message.author === 'CUSTOMER') && <Button emphasis="ghost" intent="neutral" busy={busy === 'redraft'} onClick={() => void run('redraft', () => adminFetch<SupportDetail>(`/admin/support/conversations/${conversation.id}/redraft`, { method: 'POST' }), 'El asistente redacta un borrador; aparecerá en unos segundos.', 'No pudimos pedir el borrador.')}>Pedir borrador al asistente</Button>}</div>
      </form>}
    <ConfirmDialog open={closing} title="Cerrar la conversación" action="Cerrar conversación" intent="neutral" busy={busy === 'status'} onConfirm={close} onCancel={() => setClosing(false)}>
      <p>Deja de aparecer entre las abiertas. Si el contacto escribe de nuevo, se abre sola.</p>
    </ConfirmDialog>
  </article>
}
